import { ENDPOINT_PERMISSIONS } from './endpoint-permissions';

/**
 * Translating endpoint paths into allow/deny, given a session's permission names.
 *
 * Nav entries and route metadata declare **endpoint paths**; the session carries **permission
 * names**. Everything here is the join, and it is pure so it can be unit-tested without a
 * session, a store or a render.
 */

/** The permission name an endpoint path requires, or `undefined` if the map does not cover it. */
export const permissionForPath = (path: string): string | undefined => ENDPOINT_PERMISSIONS[path];

/**
 * Whether a session holding `granted` may use `path`.
 *
 * **An unmapped path denies.** Four of the 272 known paths have no entry in the legacy map
 * (`AdminClientCacheManageRemoveIpCache`, `NotificationsSettingsPAllGet`,
 * `NotificationsApplicationsMicroservicesTemplatesBulkPost`, `NegotiatorDeals`). Treating an
 * unmapped path as "no restriction" would make a forgotten map entry silently open a screen to
 * everyone, so the safe reading is the closed one. If one of those four needs to be reachable,
 * give it a map entry rather than loosening this.
 */
export const allowsPath = (granted: ReadonlySet<string>, path: string): boolean => {
  const required = permissionForPath(path);
  return required !== undefined && granted.has(required);
};

/**
 * Whether a session may use **any** of these paths.
 *
 * This is what a nav entry needs: an entry lists the endpoints its screen calls, and seeing the
 * entry at all requires only that one of them is permitted.
 *
 * An EMPTY list means "no restriction" and allows. That is the distinction the legacy code kept
 * losing: no declared requirement is not the same as a requirement nobody meets.
 *
 * > The legacy version of this check is `VerticalNavMenuLink`, which wrote
 * > `apiPaths.forEach(p => { hasPermission = isAllowed })` — the LAST path silently decided the
 * > outcome instead of any of them. It also memoised on `[]`, so it ran once before the token
 * > had been decoded and never again. Both are fixed here; see docs/07-decisions.md D15.
 */
export const allowsAnyPath = (granted: ReadonlySet<string>, paths: readonly string[]): boolean =>
  paths.length === 0 || paths.some((path) => allowsPath(granted, path));

/** Whether a session may use **every** one of these paths. An empty list allows. */
export const allowsAllPaths = (granted: ReadonlySet<string>, paths: readonly string[]): boolean =>
  paths.every((path) => allowsPath(granted, path));

/** Every endpoint path a session may use. Useful for picking a landing route after sign-in. */
export const accessiblePaths = (granted: ReadonlySet<string>): string[] =>
  Object.keys(ENDPOINT_PERMISSIONS).filter((path) => allowsPath(granted, path));
